import { useMemo } from "react";
import { useQuery } from "react-query";
// eslint-disable-next-line import/no-unresolved -- JS query module resolved at build time
import { getCampaignCost } from "graphQL/query/campaign";
import { useCampaignData } from "campaign-builder/context/CampaignDataContext";
import { useClientConfig } from "campaign-builder/context/ClientConfigContext";
import { useTargeting } from "../../targeting";
import { resolveCityLimits } from "campaign-builder/state/targetingLimits";
import { buildCostPreview, EMPTY_COST_PREVIEW, type CampaignCostPreview, type ServerDetailedCost } from "./costPreview";
import { resolveCampaignDuration } from "./campaignDuration";

export interface ChannelCostPreview {
  /** ADM-shaped preview the shared drawer renders (single quote line + coupon). */
  campaignCostPreview: CampaignCostPreview;
  /** Server breakdown — feeds the submit city-metrics + pathfinder cart. */
  detailedCost: ServerDetailedCost | null;
  /** Campaign duration in weeks for the submit payload. */
  campaignDuration: number;
  refetchCost: () => void;
  isLoadingCost: boolean;
}

/**
 * Channel cost for the H2H/D2D order drawer. Reads the server-authoritative
 * `campaign.detailedCost(cityId)` (the same breakdown the legacy flow used, valid
 * for both channels) rather than re-deriving the per-mission formula client-side.
 * The quote collapses to one channel line; the coupon discount is applied on top.
 */
export const useChannelCostPreview = (quoteItemLabel: string): ChannelCostPreview => {
  // The route-param campaignId (string), NOT campaignData.id (coerced to a
  // number): the query key must be element-identical to TargetingContext's cost
  // query and to the invalidations fired after selection/format saves — a
  // number-keyed entry would live in a separate cache slot that those
  // invalidations never refresh, freezing the drawer quote at its first fetch.
  const { campaignData, campaignId } = useCampaignData();
  const { country } = useClientConfig();
  const { channel, selectedCity, selectedAreas } = useTargeting();

  // undefined until a city is picked — the query stays disabled, and no
  // sentinel key slot (cityId=0) ever registers in the cache.
  const cityId = selectedCity?.id;
  const countryTaxRate = country?.countryDefaultSettings?.countryTaxRate ?? 0;
  const stripeCouponCode = campaignData?.stripeCouponCode ?? null;
  const amountOff = campaignData?.amountOff ?? null;
  const percentOff = campaignData?.percentOff ?? null;

  const {
    data: detailedCost = null,
    refetch,
    isFetching,
  } = useQuery<ServerDetailedCost | null>({
    // Keyed on campaign + city only: the server breakdown is coupon-independent
    // (the discount is applied client-side in buildCostPreview), so coupon changes
    // must not trigger a redundant refetch.
    queryKey: ["channelCampaignCost", campaignId, cityId],
    // `enabled` guarantees campaignId + city exist by the time this runs.
    queryFn: () => getCampaignCost({ id: campaignId!, cityId: cityId! }),
    // No fetch before a city exists: the cityId=0 sentinel the server answers
    // with an all-zero breakdown (cached as a real quote) must never be sent.
    enabled: !!campaignId && !!selectedCity,
  });

  const campaignCostPreview = useMemo(
    () =>
      detailedCost
        ? buildCostPreview(detailedCost, quoteItemLabel, { stripeCouponCode, amountOff, percentOff }, countryTaxRate)
        : EMPTY_COST_PREVIEW,
    [detailedCost, quoteItemLabel, stripeCouponCode, amountOff, percentOff, countryTaxRate]
  );

  const weeklyMissionLimit = resolveCityLimits(country, selectedCity?.id ?? null).weeklyMissionLimit;
  const campaignDuration = useMemo(
    () => resolveCampaignDuration(channel, selectedAreas, weeklyMissionLimit),
    [channel, selectedAreas, weeklyMissionLimit]
  );

  // isFetching (not isLoading) so background refetches — e.g. after a selection
  // or format invalidation — also read as "loading" and keep payment blocked
  // instead of leaving a stale quote actionable.
  return { campaignCostPreview, detailedCost, campaignDuration, refetchCost: refetch, isLoadingCost: isFetching };
};
